import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  CircularProgress,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import {
  PageSectionKey,
  PageUid,
  useCustomisation,
} from "../../../../../context/CustomisationContext";
import { usePortal } from "../../../../../context/PortalsContext";
import { useToast } from "../../../../../context/ToastContext";
import { getSectionDesign } from "../../../../../services/api/apiClient";
import { Attribute } from "../../../../../services/api/types/portalConfiguration/portal";
import { brand, gray } from "../../../../../theme/themePrimitives";
import { toCamelCase } from "../../../../../utils/helpers";
import { AssignmentsCustomisation } from "./components/AssignmentsCustomisation";
import { BackgroundCustomisation } from "./components/BackgroundCustomisation";
import { ButtonsCustomisation } from "./components/ButtonsCustomisation";
import { CardsCustomisation } from "./components/CardsCustomisation";
import { InputFieldsSettingsCustomisation } from "./components/InputFieldsSettingsCustomisation";
import { LogoCustomisation } from "./components/LogoCustomisation";
import { NavbarCustomisation } from "./components/NavbarCustomisation";
import { TextCustomisation } from "./components/TextCustomisation";

interface CustomisationTabSectionProps<T extends string> {
  tabSection: PageUid;
  tabs: readonly T[];
}

export const CustomisationTabSection = <T extends string>({
  tabSection,
  tabs,
}: CustomisationTabSectionProps<T>) => {
  const [loading, setLoading] = useState(false);
  const { showToast } = useToast();
  const [localSearch, setLocalSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [activeTab, setActiveTab] = useState<T>(tabs[0]);

  const SEARCH_DEBOUNCE = 400;

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(localSearch.trim());
    }, SEARCH_DEBOUNCE);
    return () => clearTimeout(handle);
  }, [localSearch]);

  const filteredTabs = useMemo(
    () =>
      tabs.filter((tab) =>
        tab.toLowerCase().includes(debouncedSearch.toLowerCase()),
      ),
    [tabs, debouncedSearch],
  );
  const { portals } = usePortal();
  const { updateCustomisationSection } = useCustomisation();

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLocalSearch(event.target.value);
  };

  const handleTabChange = (tab: T) => {
    setActiveTab(tab);
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      if (portals.selectedPortal) {
        const data: Attribute[] = await getSectionDesign(
          portals.selectedPortal.portalUid,
          tabSection,
          `${toCamelCase(activeTab)}Settings`,
        );
        const updatedAttributes: Record<string, string> = {};
        data.forEach((customisation) => {
          updatedAttributes[customisation.attributeKey] =
            customisation.attributeValue;
        });
        updateCustomisationSection(
          tabSection,
          `${toCamelCase(activeTab)}Settings` as PageSectionKey,
          updatedAttributes,
        );
      }
    } catch (error: any) {
      console.log("Error:", error);
      showToast(error.response?.data || "An error occurred", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Reset activeTab when tabSection changes (which means we're switching pages)
    // This ensures we always start with the first tab when switching between pages
    if (tabs.length > 0) {
      setActiveTab(tabs[0]);
    }
  }, [tabSection, tabs]);

  useEffect(() => {
    // When filtered tabs change, ensure activeTab is still in the list
    if (filteredTabs.length > 0 && !filteredTabs.includes(activeTab)) {
      setActiveTab(filteredTabs[0]);
    }
  }, [filteredTabs, activeTab]);

  useEffect(() => {
    // Only fetch data if activeTab exists in the current tabs array and in filtered tabs.
    // This prevents fetching for the old tab when search filters change (avoids duplicate API calls).
    if (
      !activeTab ||
      !tabs.includes(activeTab) ||
      !filteredTabs.includes(activeTab) ||
      !portals.selectedPortal
    )
      return;
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    portals.selectedPortal,
    updateCustomisationSection,
    activeTab,
    tabSection,
    filteredTabs,
  ]);

  return (
    <Box mt={2} sx={{ display: "grid", gridTemplateColumns: "1fr 3fr" }}>
      <Box pr={2}>
        {/* LEFT SIDE ALWAYS RENDERED */}
        <TextField
          size="small"
          placeholder="Search components"
          variant="outlined"
          fullWidth
          value={localSearch}
          onChange={handleSearchChange}
          sx={{
            "& .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
            "& .MuiInputBase-root": {
              bgcolor: "#EAECF0",
            },
          }}
          slotProps={{
            input: {
              sx: {
                borderRadius: "12px",
                backgroundColor: `${gray[100]} !important`,
              },
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            },
          }}
        />

        <Stack spacing={1} mt={2}>
          {filteredTabs.length === 0 ? (
            <Typography variant="body2" color="text.secondary" py={2}>
              No tabs match your search.
            </Typography>
          ) : (
            filteredTabs.map((tab) => (
              <Box
                key={tab}
                onClick={() => handleTabChange(tab)}
                sx={{
                  backgroundColor: activeTab === tab ? brand[100] : "",
                  borderRadius: "6px",
                  padding: "8px 12px",
                  color: activeTab === tab ? brand[600] : gray[600],
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: gray[100],
                  },
                }}
              >
                <Typography fontWeight="600">{tab}</Typography>
              </Box>
            ))
          )}
        </Stack>
      </Box>

      {/* RIGHT SIDE */}
      <Box pl={2} borderLeft={`1px solid ${gray[200]}`}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "30vh",
            }}
          >
            <CircularProgress size={60} />
          </Box>
        ) : (
          <>
            {activeTab === "Logo" && <LogoCustomisation pageId={tabSection} />}
            {activeTab === "Background" && (
              <BackgroundCustomisation pageId={tabSection} />
            )}
            {activeTab === "Text colors" && (
              <TextCustomisation pageId={tabSection} />
            )}
            {activeTab === "Navbar" && <NavbarCustomisation />}
            {activeTab === "Buttons" && (
              <ButtonsCustomisation pageId={tabSection} />
            )}
            {activeTab === "Assignment status colors" && (
              <AssignmentsCustomisation pageId={tabSection} />
            )}
            {activeTab === "Input fields" && (
              <InputFieldsSettingsCustomisation pageId={tabSection} />
            )}
            {activeTab === "Cards" && (
              <CardsCustomisation pageId={tabSection} />
            )}
          </>
        )}
      </Box>
    </Box>
  );
};
